import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import {
  AppCard,
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppMultiSelect,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import { Tab, Tabs } from 'react-bootstrap';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../../routing/helpers/router';
import ResourceManagerSidebar from '../../../../layout/screenSpecifics/Modules/ResourceManager/Sidebar/ResourceManagerSidebar';
import colors from '../../../../constants/colors';
import Translator from '../../../../layout/components/helpers/Translator';
import minions from '../../../../minions/minions';
import SpinnerLoader from '../../../../layout/components/helpers/SpinnerLoader';
import { toFormData } from '../../../../minions/forms';
import ObligationCard from '../../../../layout/screenSpecifics/Modules/ResourceManager/Settings/Resource/ObligationCard';
import * as moduleAction from '../../../../store/actions/moduleAction';
import _ from 'lodash';
import EditorTranslatable from '@/layout/components/form/Texts/EditorTranslatable';

export default function ModuleResourceManagerSettingsResourceAddScreen() {
  const { moduleBridgeHex, templateHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('obligations');
  const [template, setTemplate] = useState();
  const [initialValues, setInitialValues] = useState();
  const [validationSchema, setValidationSchema] = useState();

  // PARA CAMBIAR A FUTURO A COMPONENTE O SIMILAR
      if (!moduleBridgeHex || !moduleData) {
        <AppNoInfo />;
      }
      // > LOAD MODULE DATA //
      const loadModuleData = useCallback(async () => {
        dispatch(
          moduleAction.fetch(
            axiosPath('api_r_user_module_resourceManager_getModuleData'),
            'moduleData',
            {
              moduleBridgeHex,
            },
          ),
        );
      }, []);
    
      useEffect(() => {
          !moduleData && loadModuleData();
        }, []);
      // > LOAD MODULE DATA //
      // < STORE CALLS
      const loadModulePermissions = useCallback(async () => {
        try {
          await dispatch(
            moduleAction.fetch(
              axiosPath('api_r_user_module_resourceManager_getPermissions'),
              'permissions',
              {
                moduleBridgeHex,
                subType: 'permissions',
              },
            ),
          );
          await dispatch(
            moduleAction.fetch(
              axiosPath('api_r_user_module_resourceManager_getPermissions'),
              'userBridge',
              {
                moduleBridgeHex,
                subType: 'userBridge',
              },
            ),
          );
        } catch (err) {}
      }, [moduleBridgeHex]);
    
      useEffect(() => {
        loadModulePermissions();
      }, [moduleBridgeHex]);
      // < STORE CALLS

  // > LOAD TEMPLATE
  const loadTemplate = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_settings_resourceTemplate_get',
          ),
          {
            moduleBridgeHex,
            templateHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setTemplate(data);

            const iv = {
              name: '',
              description: {},
              files: [],
              plant: null,
            };

            const schema = {
              name: Yup.string().required(
                <Translator file="client" name="client.please-enter-name" />,
              ),
            };

            data?.features?.forEach((f) => {
              let defaultValue = '';

              switch (f?.type) {
                case 0:
                case 1:
                  if (f?.quantity > 0) {
                    schema[`feature${f?.hex}`] = Yup.string().max(
                      f?.quantity,
                      <Translator
                        file="module-resourceManager"
                        name="resource.maximum.quantity.characters.permitted"
                        variables={{
                          quantity: f?.quantity,
                        }}
                      />,
                    );
                  }
                  break;
                case 4:
                  defaultValue = null;
                  schema[`feature${f?.hex}`] = Yup.number().nullable();
                  break;
                case 5:
                  defaultValue = [];
                  schema[`feature${f?.hex}`] = Yup.array()
                    .of(
                      Yup.number().typeError(
                        <Translator
                          file="module-resourceManager"
                          name="resource.each.element.must.be.number"
                        />,
                      ),
                    )
                    .notRequired();
                  break;
                default:
                  break;
              }

              iv[`feature${f?.hex}`] = defaultValue;
            });

            iv.selectedObligationIDs = data?.selectedObligationIDs;

            setInitialValues(iv);
            setValidationSchema(Yup.lazy(() => Yup.object().shape(schema)));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, templateHex],
  );

  useEffect(() => {
    loadTemplate();
  }, []);
  // < LOAD TEMPLATE

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath('api_r_user_module_resourceManager_settings_resource_add'),
          toFormData({
            moduleBridgeHex,
            templateHex,
            ...values,
          }),
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        .then(() => {
          navigate(
            path({
              name: 'user_module_resourceManager_settings_resourceTemplate_list',
              params: { moduleBridgeHex },
            }),
          );
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, templateHex],
  );
  // < SAVE

  const renderFeatureInputs = useCallback(
    (values, errors, setFieldValue) =>
      template?.features?.map((f) => {
        switch (f?.type) {
          case 0:
            return (
              <AppInput
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                isClearable
              />
            );
          case 1:
            return (
              <AppTextArea
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                isClearable
              />
            );
          case 2:
            return (
              <AppInputNumber
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                decimals={f?.quantity > 0 ? f?.quantity : 99}
                isClearable
              />
            );
          case 3:
            return (
              <AppDatePicker
                title={f?.name}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(v) => setFieldValue(`feature${f?.hex}`, v)}
                isClearable
                hasToggleType
              />
            );
          case 4:
            return (
              <AppSingleSelect
                title={f?.name}
                options={f?.options}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(opt) => setFieldValue(`feature${f?.hex}`, opt)}
                isClearable
                hasSearchBox
              />
            );
          case 5:
            return (
              <AppMultiSelect
                title={f?.name}
                options={f?.options}
                value={values?.[`feature${f?.hex}`]}
                error={errors?.[`feature${f?.hex}`]}
                onChange={(opt) => setFieldValue(`feature${f?.hex}`, opt)}
                hasSearchBox
                hasSelectOptions
              />
            );
          default:
            return null;
        }
      }),
    [template],
  );

  const handleUnSelectAllObligations = useCallback(
    (obligations, selectedObligationIDs, setFieldValue) =>
      setFieldValue(
        'selectedObligationIDs',
        _.difference(selectedObligationIDs, _.map(obligations, 'id')),
      ),
    [],
  );

  const handleSelectAllObligations = useCallback(
    (obligations, selectedObligationIDs, setFieldValue) =>
      setFieldValue(
        'selectedObligationIDs',
        _.union(selectedObligationIDs, _.map(obligations, 'id')),
      ),
    [],
  );

  if (isLoading) {
    return (
      <AppNoInfo color={colors.primaryLight}>
        <SpinnerLoader size={30} />
      </AppNoInfo>
    );
  }

  return (
    <>
      <ResourceManagerSidebar activeRoute="user_module_resourceManager_settings" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData?.moduleName}`,
                  route: {
                    name: 'user_module_resourceManager_resource_home',
                    params: { moduleBridgeHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-resourceManager"
                      name="resource.settings"
                    />,
                  ),
                  route: {
                    name: 'user_module_resourceManager_settings_resourceTemplate_list',
                    params: { moduleBridgeHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-resourceManager"
                      name="resource.new.asset"
                    />,
                  ),
                },
              ]
            : [
                {
                  name: '...',
                },
              ]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <AppCard
            header={
              <Translator
                file="module-resourceManager"
                name="resource.asset.creation"
              />
            }
          >
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={handleSave}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className="row mb-5">
                    <div className="col-xs-12 col-lg-6">
                      <AppInput
                        title={
                          <Translator
                            file="module-resourceManager"
                            name="resource.name"
                          />
                        }
                        onChange={(value) => setFieldValue('name', value)}
                        value={values.name}
                        error={errors.name}
                        isClearable
                      />
                      <EditorTranslatable
                        title={
                          <Translator
                            file="module-resourceManager"
                            name="resource.description"
                          />
                        }
                        icon={<AppRemixIcon icon="file-text" />}
                        onChange={(opt) => setFieldValue('description', opt)}
                        value={values.description}
                        className=""
                        toolbar={{
                          toolbar: [
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ color: [] }, { background: [] }],
                            ['clean'],
                            [{ direction: 'rtl' }],
                          ],
                        }}
                        height={200}
                      />
                      <AppSingleSelect
                        placeholder={
                          <Translator
                            file="module-resourceManager"
                            name="resource.select.installation"
                          />
                        }
                        options={template?.plants}
                        icon={<AppRemixIcon icon="building-4" />}
                        onChange={(value) => setFieldValue('plant', value)}
                        value={values.plant}
                        error={errors.plant}
                        hasSearchBox
                        isClearable
                      />
                      <AppFileUploader
                        isMulti
                        placeholder={
                          <Translator
                            file="module-resourceManager"
                            name="resource.select.one.or.more.files"
                          />
                        }
                        onChange={(opt) =>
                          setFieldValue('files', [...values.files, ...opt])
                        }
                      />
                      <FileList>
                        {values?.files.map((f, i) => (
                          <li
                            key={i}
                            title={
                              <Translator
                                file="module-resourceManager"
                                name="resource.click.delete"
                              />
                            }
                            onClick={() =>
                              setFieldValue(
                                'files',
                                values?.files?.filter(
                                  (f, index) => index !== i,
                                ),
                              )
                            }
                          >
                            <AppRemixIcon
                              className="pointer mr-3"
                              icon="delete-bin"
                              color={colors.danger}
                            />
                            {minions.files.fileIcon(
                              f?.name,
                              true,
                              null,
                              'mr-2',
                            )}
                            <FileName>{f?.name}</FileName>
                          </li>
                        ))}
                      </FileList>
                    </div>
                    <div className="col-xs-12 col-lg-6">
                      {renderFeatureInputs(values, errors, setFieldValue)}
                    </div>
                  </div>
                  <Tabs
                    defaultActiveKey={activeTab}
                    onSelect={(v) => setActiveTab(v)}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Tab
                      eventKey="obligations"
                      title={
                        <Translator
                          file="module-resourceManager"
                          name="resource.obligations"
                        />
                      }
                      className="p-3 border border-top-0 bg-white"
                    >
                      <div>
                        <span
                          className="btn btn-xs btn-outline-light mr-2 mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.select.all"
                            />,
                          )}
                          onClick={() =>
                            handleSelectAllObligations(
                              template?.legalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue,
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple" />
                        </span>
                        <span
                          className="btn btn-xs btn-outline-light mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.deselect.all"
                            />,
                          )}
                          onClick={() =>
                            handleUnSelectAllObligations(
                              template?.legalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue,
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple-blank" />
                        </span>
                      </div>
                      {template?.legalObligations?.length > 0 ? (
                        <ul className="list-group">
                          {template?.legalObligations?.map((o) => (
                            <ObligationCard
                              key={o?.id}
                              obligation={o}
                              selectedObligationIDs={
                                values?.selectedObligationIDs
                              }
                              setFieldValue={setFieldValue}
                            />
                          ))}
                        </ul>
                      ) : (
                        <div className="alert alert-info">
                          No hay obligaciones legales asignadas
                        </div>
                      )}
                    </Tab>
                    <Tab
                      eventKey="internalObligations"
                      title={
                        <Translator
                          file="module-resourceManager"
                          name="resource.internal.obligations"
                        />
                      }
                      className="p-3 border border-top-0 bg-white"
                    >
                      <div>
                        <span
                          className="btn btn-xs btn-outline-light mr-2 mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.select.all"
                            />,
                          )}
                          onClick={() =>
                            handleSelectAllObligations(
                              template?.internalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue,
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple" />
                        </span>
                        <span
                          className="btn btn-xs btn-outline-light mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.deselect.all"
                            />,
                          )}
                          onClick={() =>
                            handleUnSelectAllObligations(
                              template?.internalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue,
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple-blank" />
                        </span>
                      </div>
                      {template?.internalObligations?.length > 0 ? (
                        <ul className="list-group">
                          {template?.internalObligations?.map((o) => (
                            <ObligationCard
                              key={o?.id}
                              obligation={o}
                              selectedObligationIDs={
                                values?.selectedObligationIDs
                              }
                              setFieldValue={setFieldValue}
                            />
                          ))}
                        </ul>
                      ) : (
                        <div className="alert alert-info">
                          <Translator
                            file="module-resourceManager"
                            name="no.internal.obligations.available"
                          />
                        </div>
                      )}
                    </Tab>
                  </Tabs>
                  <div className="mt-3 float-right">
                    <button
                      type="submit"
                      className="btn btn-xs btn-success"
                      disabled={isSubmitting}
                    >
                      <Translator
                        file="module-resourceManager"
                        name="resource.create"
                      />
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </AppCard>
        </div>
      </main>
    </>
  );
}

const FileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.uploaded ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
const FileName = styled.span`
  text-decoration: ${(props) => (props.isRemoved ? 'line-through' : 'unset')};
`;
